import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface Props {
  value: string;                 // 'yyyy-MM-dd' or '' when empty
  onChange: (v: string) => void;
  min?: string;                  // earliest selectable day (the From value, for a To picker)
  max?: string;                  // latest selectable day (the To value, for a From picker)
  ariaLabel?: string;
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const WD = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const pad = (n: number) => String(n).padStart(2, '0');
const toStr = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parse = (s?: string): Date | null => {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};
// Display format for a chosen date: US MM/dd/yyyy (underlying value stays ISO).
const fmtUS = (s: string): string => {
  const d = parse(s);
  return d ? `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()}` : '';
};
const sameDay = (a: Date, b: Date | null) =>
  !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/**
 * Custom date picker for the "Query Samples" dialog. Replaces the native
 * <input type="date"> so the calendar's selected-day highlight is BTSoft orange
 * instead of the browser's hard-coded blue (Chromium ignores accent-color on the
 * native calendar popup). Same look as the native picker — month header with
 * up/down nav, weekday row, day grid, Clear / Today footer — fully restyleable.
 */
export function QueryDatePicker({ value, onChange, min, max, ariaLabel }: Props) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const selected = parse(value);
  const minD = parse(min);
  const maxD = parse(max);
  const [view, setView] = useState(() => {
    const base = selected ?? new Date();
    return { y: base.getFullYear(), m: base.getMonth() };
  });

  const openCal = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + window.scrollY + 4, left: r.left + window.scrollX });
    const base = parse(value) ?? new Date();
    setView({ y: base.getFullYear(), m: base.getMonth() }); // re-centre on the chosen day
    setOpen((o) => !o);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const prevMonth = () => setView((v) => (v.m === 0 ? { y: v.y - 1, m: 11 } : { y: v.y, m: v.m - 1 }));
  const nextMonth = () => setView((v) => (v.m === 11 ? { y: v.y + 1, m: 0 } : { y: v.y, m: v.m + 1 }));

  // 6×7 grid starting on the Sunday on/before the 1st of the month.
  const startOffset = new Date(view.y, view.m, 1).getDay();
  const gridStart = new Date(view.y, view.m, 1 - startOffset);
  const days = Array.from({ length: 42 }, (_, i) =>
    new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i));

  const disabled = (d: Date) => (!!minD && d < minD) || (!!maxD && d > maxD);
  const pick = (d: Date) => { onChange(toStr(d)); setOpen(false); };
  const today = new Date();

  return (
    <>
      <button ref={btnRef} type="button" aria-label={ariaLabel}
        className={`qs-dpick${value ? '' : ' qs-empty'}`} onClick={openCal}>
        <span className="qs-dpick-val">{value ? fmtUS(value) : 'mm/dd/yyyy'}</span>
        <i className="ti ti-calendar qs-dpick-ic" />
      </button>

      {open && createPortal(
        <>
          <div className="qs-dpick-backdrop" onMouseDown={() => setOpen(false)} />
          <div className="qs-cal" style={{ top: pos.top, left: pos.left }}>
            <div className="qs-cal-hdr">
              <span className="qs-cal-title">{MONTHS[view.m]} {view.y}</span>
              <div className="qs-cal-nav">
                <button type="button" onClick={prevMonth} aria-label="Previous month">
                  <i className="ti ti-chevron-up" />
                </button>
                <button type="button" onClick={nextMonth} aria-label="Next month">
                  <i className="ti ti-chevron-down" />
                </button>
              </div>
            </div>
            <div className="qs-cal-wd">{WD.map((w) => <span key={w}>{w}</span>)}</div>
            <div className="qs-cal-grid">
              {days.map((d, i) => {
                const out = d.getMonth() !== view.m;
                const sel = sameDay(d, selected);
                const tod = sameDay(d, today);
                return (
                  <button key={i} type="button" disabled={disabled(d)}
                    className={`qs-cal-day${out ? ' out' : ''}${sel ? ' sel' : ''}${tod && !sel ? ' today' : ''}`}
                    onClick={() => pick(d)}>{d.getDate()}</button>
                );
              })}
            </div>
            <div className="qs-cal-foot">
              <button type="button" className="qs-cal-link"
                onClick={() => { onChange(''); setOpen(false); }}>Clear</button>
              <button type="button" className="qs-cal-link" onClick={() => pick(new Date())}>Today</button>
            </div>
          </div>
        </>,
        document.body,
      )}
    </>
  );
}
